import React, {useState} from 'react';
import * as yup from 'yup';
import {useDispatch, useSelector} from 'react-redux';
import {Controller, useForm} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import {notifySuccess} from '../../../helpers/notifySnack';
import Head from '../../Components/Profile/Head';
import Languages from '../../Components/Profile/Languages';
import InputMUI from '../../../shared/InputMUI';
import ButtonMUI from '../../../shared/ButtonMUI';
import {postProfilePassword} from '../../../redux/actions/profile/profileActions';
import {useTranslation} from 'react-i18next';
import passIcon from '../../../assets/icons/pass.svg';
import './UserSettings.scss';
import {passwordRegex} from "../../../helpers/constants";

const UserSettings = () => {
    const {profileMe} = useSelector((state) => state.profile);

    const {t} = useTranslation();

    const dispatch = useDispatch();
    const [buttonLoad, setButtonLoad] = useState(false);

    const schema = yup.object({
        passwordOld: yup
            .string()
            .min(8, t('profileSettings.passwordMinLength'))
            .required(t('profileSettings.requiredField')),
        passwordNew: yup
            .string().matches(passwordRegex, t('validations.password'))
            .required('validations.required'),
        passwordNewConfirm: yup
            .string()
            .oneOf([yup.ref('passwordNew'), null], t('profileSettings.passwordMismatch'))
            .required(t('profileSettings.requiredField'))
    });

    const {
        control,
        handleSubmit,
        reset,
        formState: {errors, isValid}
    } = useForm({
        mode: 'all',
        reValidateMode: 'onChange',
        shouldFocusError: true,
        resolver: yupResolver(schema),
        defaultValues: {
            passwordOld: '',
            passwordNew: '',
            passwordNewConfirm: ''
        }
    });

    const onSubmit = async (data) => {
        try {
            setButtonLoad(true);

            const formData = new FormData();
            formData.append('oldPassword', data.passwordOld);
            formData.append('password', data.passwordNew);
            formData.append('password_confirmation', data.passwordNewConfirm);

            const res = await dispatch(postProfilePassword(formData));

            if (res.payload?.status === 200) {
                notifySuccess(t('profileSettings.passwordChangeSuccess'));
                reset();
            }
        } finally {
            setButtonLoad(false);
        }
    };

    return (
        <div className='user-settings'>
            <Head title={t('profileSettings.title')}/>
            <div className='user-settings__content'>
                <form className='user-settings__form' onSubmit={handleSubmit(onSubmit)}>
                    <Languages/>
                    {profileMe?.role !== 'demo' && (
                        <div className='user-settings__block input'>
                            <div className='user-settings__block-title'>{t('profileSettings.changePassword')}</div>

                            <div className='user-settings__input'>
                                <div className='form__input'>
                                    <Controller
                                        name='passwordOld'
                                        control={control}
                                        render={({field}) => (
                                            <InputMUI
                                                type='password'
                                                fullWidth
                                                error={errors.passwordOld?.message}
                                                inputProps={field}
                                                label={t('profileSettings.currentPassword')}
                                                startAdornment={passIcon}
                                            />
                                        )}
                                    />
                                </div>
                                <div className='form__input'>
                                    <Controller
                                        name='passwordNew'
                                        control={control}
                                        render={({field}) => (
                                            <InputMUI
                                                type='password'
                                                fullWidth
                                                error={errors.passwordNew?.message}
                                                inputProps={field}
                                                label={t('profileSettings.newPassword')}
                                                startAdornment={passIcon}
                                            />
                                        )}
                                    />
                                </div>
                                <div className='form__input'>
                                    <Controller
                                        name='passwordNewConfirm'
                                        control={control}
                                        render={({field}) => (
                                            <InputMUI
                                                type='password'
                                                fullWidth
                                                error={errors.passwordNewConfirm?.message}
                                                inputProps={field}
                                                label={t('profileSettings.confirmNewPassword')}
                                                startAdornment={passIcon}
                                            />
                                        )}
                                    />
                                </div>
                            </div>

                            <ButtonMUI
                                type='submit'
                                color='primary'
                                className='user-settings__submit-btn'
                                loading={buttonLoad}
                                disabled={!isValid}
                            >
                                {t('profileSettings.submit')}
                            </ButtonMUI>
                        </div>
                    )}
                </form>
            </div>
        </div>
    );
};

export default UserSettings;
